import type { ShortVideoProperty } from '~/core/entities/property';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { mapPropertyToShortVideo } from '~/composables/shorts/mappers';
import { useLogger } from '~/composables/useLogger';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

export interface ShortVideo {
	property: ShortVideoProperty;
	videoUrl: string | null;
	thumbnailUrl: string | null;
	images?: string[];
	videoWidth?: number;
	videoHeight?: number;
}

export interface FetchShortsResult {
	items: ShortVideo[];
	hasMore: boolean;
	allWatched?: boolean;
	totalAvailable?: number;
}

const PER_PAGE = 15;

export function useFetchShorts() {
	const logger = useLogger('useFetchShorts');
	const { user } = useUserSession();
	const getUserId = () => {
		if (!import.meta.client) return undefined;
		return user.value?.id;
	};

	const getCookieData = useCookieData();

	async function fetchVideos(
		filters: ShortsFilters = {},
		page: number = 1,
		perPage: number = PER_PAGE
	): Promise<FetchShortsResult> {
		const [result, error] = await nuxtPropertiesRepository.getShorts(
			{ ...filters, markUserWishlistsId: getUserId(), page, perPage },
			getCookieData()
		);

		if (error || !result) {
			logger.error('Failed to fetch shorts', { error, filters });
			return { items: [], hasMore: false };
		}

		const items = (result.properties ?? [])
			.map(mapPropertyToShortVideo)
			.filter((item): item is ShortVideo => item !== null);

		const pagination = result.pagination;
		const hasMorePages =
			(pagination?.page ?? 1) < (pagination?.totalPages ?? 1);

		return {
			items,
			hasMore: hasMorePages,
			allWatched: result.allWatched,
			totalAvailable:
				result.totalAvailable ?? pagination?.filteredCount ?? items.length
		};
	}

	return { fetchVideos };
}
